// 請われぬ介錯の「執行」と「砲の弾」の見た目 — 作者が手本の絵で選んだ形をコードで描く (2026-10-20)。
//
// 作者の選択 = 答えるページ (10-20 07:10 UTC・台帳 docs/ledger/executioner.md・手本 そざい/concept/exec-fx-1020/)。
//   執行 (executioner_finisher) = A「刃に宿る紫」: 振り上げた刃全体に紫の炎がまとわりつき、叩き下ろすと床に紫の炎が噴き出して
//     石のかけらが飛ぶ。床には紫の残り火と瓦礫が残り、後ろへ払う刃にも炎が残って欠片が散る。
//   砲の弾 (exec_cannonShot / exec_cannonVolley) = C「紫の残り火」: 撃つ瞬間に砲口から紫の炎が噴き、弾は黒い鉄の球で
//     後ろに紫の炎の尾がたなびく。弾くと球が割れて黒い石片になり、紫の炎の欠片が散る。
//   発勁 (A「重ね輪」) は src/fajin_fx.ts の look 'j'。
// 段 (執行・据え斬りの絵 sue_*): 予告 600ms = sue_raise の刃に炎が 300ms で燃え上がる。当たり 250ms の 0.35 (87.5ms) が床を打つ瞬間で、
//   ここから 260ms 床の炎が噴き、石のかけらが放物線で飛んで床に落ちる。残り火は打ってから約 1 秒で消える。
//   払い (sue_flick) の間も刃の炎は残り、上へ欠片を散らす。待機の絵へ戻ったら刃の炎は 150ms で消える。
// 描くだけ。当たり (場所・時間・ダメージ) と合図の色の決まり (紫＝ボスの致命・弾ける弾) は変えない。白い線は描かない (炎も面で描く)。
// 前の配布版 (執行＝炎なしの据え斬り・弾＝紫の光の玉) は開発版の `?execFlame=old` と `?execShot=old`。
import type { Boss } from './types'
import { glowMote, hash01 } from './fx_kit'
import { finisherRaiseStartMs } from './exec_finisher_step'
import { et } from './exec_tint'
import { projectBossSpriteSourcePoint, type BossSpritePlacement } from './sprites'
import {
  BURST_ANCHOR_Y, BURST_FRAMES, FLAME_ANCHOR_Y, burstSprite, drawAdditive, execFlameSpritesReady, flameSprite, preloadExecFxSprites,
} from './exec_fx_sprites'

export type ExecFxMode = 'mihon' | 'old'
// 10-20 の2回目: 作者が描き直しの手本から 執行＝prevA・発勁＝j4 を選んだ。最初に作った版 (炎の舌が太く明るい・重ね輪) は 'v1'。
// 10-20 の3回目: 炎・輪の質感を手描きの絵にした版 'sprite' (src/exec_fx_sprites.ts)。10-21 作者「紫を直したら入れる」で配布版の既定へ。
//   テストだけはコードの版 (絵が読めない)。前の配布版 (コードで描く手本の版) は ?execFlame=code。
let flameFx: ExecFxMode | 'v1' | 'sprite' = defaultFlameFx()
let shotFx: ExecFxMode = 'mihon'

function defaultFlameFx(): 'mihon' | 'sprite' { return import.meta.env?.MODE !== 'test' ? 'sprite' : 'mihon' }
/** game.ts の URL ブロックだけが呼ぶ。指定なし (null)・知らない値は既定 (配布版・開発版とも絵の版・テストだけコードの版)。
 *  `code` = コードで描く手本の版 (10-21 までの配布版)・`sprite` = 絵の版・`v1` = 最初の版・`old` = 炎なし。 */
export function setExecFlameFx(v: string | null): void {
  flameFx = v === 'old' ? 'old' : v === 'v1' ? 'v1' : v === 'code' ? 'mihon' : v === 'sprite' ? 'sprite' : defaultFlameFx()
  if (flameFx === 'sprite') preloadExecFxSprites()
}
export function setExecShotFx(v: string | null): void { shotFx = v === 'old' ? 'old' : 'mihon' }
export function execFlameFxOn(): boolean { return flameFx !== 'old' }
/** 最初に作った版の執行の炎と発勁の重ね輪 (DEV ?execFlame=v1)。 */
export function execFxV1(): boolean { return flameFx === 'v1' }
/** 炎・発勁の輪を絵で描く版 (10-21 から配布版の既定・?execFlame=sprite)。絵が読めていない間は呼ぶ側がコードの版で描く。 */
export function execFxSprite(): boolean { return flameFx === 'sprite' }
export function execShotFxOn(): boolean { return shotFx === 'mihon' }

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }

// ---------------------------------------------------------------- 炎の舌 (共通)

/** 根元 (x,y) から (dirX,dirY) へ伸びる、揺れる炎の舌を面で描く (加算)。線は引かない。
 *  色は根元が明るい芯・先が深い紫で透明へ抜ける。sway = 揺れの位相 (秒で回す)。 */
export function flameTongue(
  ctx: CanvasRenderingContext2D, x: number, y: number, dirX: number, dirY: number,
  len: number, width: number, sway: number, alpha: number,
  core: string, mid: string, deep: string,
): void {
  if (alpha <= 0.01 || len <= 1 || width <= 0.5) return
  const mag = Math.hypot(dirX, dirY) || 1
  const ux = dirX / mag, uy = dirY / mag
  const px = -uy, py = ux
  const left: [number, number][] = [], right: [number, number][] = []
  const N = 9
  for (let i = 0; i <= N; i += 1) {
    const s = i / N
    const bend = Math.sin(s * 3.4 + sway) * width * 0.55 * s + Math.sin(s * 7.1 - sway * 1.7) * width * 0.12 * s
    const cx = x + ux * len * s + px * bend
    const cy = y + uy * len * s + py * bend
    const half = width * 0.5 * (1 - s) ** 0.75 * (s < 0.18 ? 0.55 + s / 0.18 * 0.45 : 1)
    left.push([cx + px * half, cy + py * half])
    right.push([cx - px * half, cy - py * half])
  }
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = Math.min(1, alpha)
  const g = ctx.createLinearGradient(x, y, x + ux * len, y + uy * len)
  g.addColorStop(0, core)
  g.addColorStop(0.3, mid)
  g.addColorStop(0.75, deep)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.moveTo(left[0]![0], left[0]![1])
  for (let i = 1; i < left.length; i += 1) ctx.lineTo(left[i]![0], left[i]![1])
  for (let i = right.length - 1; i >= 0; i -= 1) ctx.lineTo(right[i]![0], right[i]![1])
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

/** 宙を舞う紫の欠片 (細長い菱形の面・加算)。手本の「紫の欠片」。 */
function fleck(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rot: number, color: string, alpha: number): void {
  if (alpha <= 0.01 || size <= 0.3) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.translate(x, y)
  ctx.rotate(rot)
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(size * 1.6, 0)
  ctx.lineTo(0, size * 0.55)
  ctx.lineTo(-size * 1.1, 0)
  ctx.lineTo(0, -size * 0.55)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
  glowMote(ctx, x, y, size * 2.2, color, alpha * 0.35)
}

/** 黒い石のかけら (通常合成の面＋紫の照り返し)。 */
function stoneShard(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number, seed: number, alpha: number, rim: string): void {
  if (alpha <= 0.01 || r <= 0.4) return
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(rot)
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.beginPath()
  const n = 5
  for (let i = 0; i < n; i += 1) {
    const a = (i / n) * Math.PI * 2 + hash01(seed + i) * 0.7
    const rr = r * (0.62 + hash01(seed + i + 9) * 0.5)
    const px = Math.cos(a) * rr, py = Math.sin(a) * rr * 0.8
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
  const g = ctx.createLinearGradient(-r, -r, r, r)
  g.addColorStop(0, '#4a4650')
  g.addColorStop(0.5, '#25222b')
  g.addColorStop(1, '#0d0b10')
  ctx.fillStyle = g
  ctx.fill()
  ctx.restore()
  glowMote(ctx, x, y + r * 0.2, r * 1.6, rim, alpha * 0.35)
}

// ---------------------------------------------------------------- 執行 (A「刃に宿る紫」)

// 致命の紫 (ボスの致命の合図・弾けるかどうかの比較版で写さない)。
const FIN_CORE = 'rgba(236,214,255,0.95)'
const FIN_MID = 'rgba(176,96,236,0.85)'
const FIN_DEEP = 'rgba(92,24,150,0.55)'
const FIN_FLECK = '#c48cff'

// 据え斬りの3枚の刃の根元と先 (絵の画素・刃の灰色の画素の端を測った値)。half = 刃の半幅 (絵の画素)。
export const SUE_BLADE_LINE: Record<string, { base: readonly [number, number]; tip: readonly [number, number]; half: number }> = {
  sue_raise: { base: [292, 640], tip: [307, 12], half: 48 },
  sue_contact: { base: [92, 330], tip: [14, 985], half: 52 },
  sue_flick: { base: [565, 345], tip: [1058, 836], half: 44 },
}

const FINISHER_ID = 'executioner_finisher'
const CONTACT_FRAC = 0.35 // 据え斬りの時間割で、当たりのこの割合で sue_contact (床を打つ絵) へ替わる。
const IMPACT_LIFE_MS = 1050
const BURST_MS = 260

type Impact = { x: number; y: number; floorY: number }
let impactCache: Impact | null = null

/** 床を打ってからの ms。打つ前・執行でない時は null。 */
export function finisherImpactAgeMs(boss: Pick<Boss, 'state' | 'stateElapsed' | 'currentAttack'>): number | null {
  const atk = boss.currentAttack
  if (!atk || atk.id !== FINISHER_ID) return null
  const contactMs = atk.activeMs * CONTACT_FRAC
  if (boss.state === 'active') {
    const a = boss.stateElapsed * 1000 - contactMs
    return a >= 0 ? a : null
  }
  if (boss.state === 'recovery') return atk.activeMs - contactMs + boss.stateElapsed * 1000
  return null
}

/** 刃の炎の強さ (0..1)。予告で燃え上がり、打った後は少し落ちて払いの間も残る。 */
export function finisherBladeFlameStrength(boss: Pick<Boss, 'state' | 'stateElapsed' | 'currentAttack'>): number {
  const atk = boss.currentAttack
  if (!atk || atk.id !== FINISHER_ID) return 0
  const ms = boss.stateElapsed * 1000
  // 寄りの一歩 (src/exec_finisher_step.ts) の間は灯さず、刃を掲げてから灯す。
  if (boss.state === 'telegraph') return clamp01((ms - finisherRaiseStartMs(atk)) / 300) ** 0.7
  if (boss.state === 'active') return 1
  if (boss.state === 'recovery') {
    const flickEnd = atk.recoveryMs * 0.8
    if (ms < flickEnd) return 1 - 0.35 * clamp01(ms / flickEnd)
    return 0.65 * (1 - clamp01((ms - flickEnd) / 150))
  }
  return 0
}

/** render.ts の本体描画の直後に呼ぶ。placement = いま描いた本体の絵の置き方。 */
export function drawExecFinisherFlame(
  ctx: CanvasRenderingContext2D, boss: Boss, placement: BossSpritePlacement | null,
  timeSec: number, floorY: number, visibility: number,
): void {
  if (!execFlameFxOn() || boss.id !== 'the-executioner' || visibility <= 0.01) return
  const atk = boss.currentAttack
  if (!atk || atk.id !== FINISHER_ID) { impactCache = null; return }
  if (boss.state === 'telegraph') impactCache = null
  const line = placement ? SUE_BLADE_LINE[placement.imageKey] : undefined
  let base: { x: number; y: number } | null = null
  let tip: { x: number; y: number } | null = null
  let halfPx = 0
  if (placement && line) {
    base = projectBossSpriteSourcePoint(placement, line.base)
    tip = projectBossSpriteSourcePoint(placement, line.tip)
    const edge = projectBossSpriteSourcePoint(placement, [line.base[0] + line.half, line.base[1]])
    if (base && edge) halfPx = Math.abs(edge.x - base.x)
    if (placement.imageKey === 'sue_contact' && tip) impactCache = { x: tip.x, y: Math.min(tip.y, floorY), floorY }
  }
  // 床の噴き上がり・瓦礫・残り火 (刃の絵に関係なく、打った所に残す)。
  const impactAge = finisherImpactAgeMs(boss)
  const sprites = execFxSprite() && execFlameSpritesReady()
  if (impactAge !== null && impactCache && impactAge < IMPACT_LIFE_MS) {
    ;(sprites ? drawFinisherImpactSprite : execFxV1() ? drawFinisherImpactV1 : drawFinisherImpact)(ctx, impactCache, impactAge, timeSec, visibility)
  }
  const strength = finisherBladeFlameStrength(boss) * visibility
  if (base && tip && strength > 0.01) {
    ;(sprites ? drawBladeFlameSprite : execFxV1() ? drawBladeFlameV1 : drawBladeFlame)(ctx, base, tip, Math.max(4, halfPx), strength, timeSec, placement!.imageKey === 'sue_flick')
  }
}

function drawBladeFlameV1(
  ctx: CanvasRenderingContext2D, base: { x: number; y: number }, tip: { x: number; y: number },
  half: number, strength: number, t: number, flick: boolean,
): void {
  const len = Math.hypot(tip.x - base.x, tip.y - base.y)
  if (len < 4) return
  const ux = (tip.x - base.x) / len, uy = (tip.y - base.y) / len
  const nx = -uy, ny = ux
  // 刃全体の淡い紫の照り (面)。
  for (let i = 0; i <= 6; i += 1) {
    const s = 0.1 + (i / 6) * 0.85
    glowMote(ctx, base.x + ux * len * s, base.y + uy * len * s, half * 2.6, FIN_DEEP, 0.32 * strength)
  }
  // 刃に沿って揺れる炎の舌。炎は上へ昇る (払いの間は少し後ろへ流れる)。
  const count = 16
  for (let i = 0; i < count; i += 1) {
    const seed = i * 13.7
    const s = 0.06 + (i / (count - 1)) * 0.92
    const side = i % 2 === 0 ? 1 : -1
    const off = half * (0.55 + hash01(seed) * 0.5) * side
    const x = base.x + ux * len * s + nx * off
    const y = base.y + uy * len * s + ny * off
    const flicker = 0.75 + 0.25 * Math.sin(t * (9 + hash01(seed + 2) * 6) + seed)
    const taper = s > 0.85 ? 1 - (s - 0.85) / 0.15 * 0.5 : 1
    const fl = (half * 2.2 + hash01(seed + 4) * half * 2.2) * flicker * taper * (0.6 + strength * 0.4)
    const dx = (flick ? -ux * 0.35 : 0) + (hash01(seed + 6) - 0.5) * 0.35
    flameTongue(ctx, x, y, dx, -1, fl, half * (0.9 + hash01(seed + 8) * 0.6), t * 7 + seed,
      strength * (0.55 + hash01(seed + 10) * 0.35), FIN_CORE, FIN_MID, FIN_DEEP)
  }
  // 舞い上がる紫の欠片。
  const flecks = flick ? 12 : 8
  for (let i = 0; i < flecks; i += 1) {
    const seed = 500 + i * 7.3
    const life = (t * (0.9 + hash01(seed) * 0.6) + hash01(seed + 1)) % 1
    const s = 0.1 + hash01(seed + 2) * 0.88
    const x = base.x + ux * len * s + nx * half * (hash01(seed + 3) - 0.5) * 3 + (hash01(seed + 4) - 0.5) * 40 * life
    const y = base.y + uy * len * s - life * (40 + hash01(seed + 5) * 50)
    fleck(ctx, x, y, 2 + hash01(seed + 6) * 2.5, t * 3 + seed, FIN_FLECK, strength * (1 - life) * 0.9)
  }
}

function drawFinisherImpactV1(ctx: CanvasRenderingContext2D, at: Impact, ageMs: number, t: number, vis: number): void {
  const fy = at.floorY
  const x = at.x
  // 噴き上がり: 0〜BURST_MS で床から扇状に炎が噴き、縮んでいく。
  if (ageMs < BURST_MS + 120) {
    const p = clamp01(ageMs / BURST_MS)
    const grow = ageMs < 50 ? ageMs / 50 : 1 - clamp01((ageMs - 50) / (BURST_MS + 70)) ** 1.4
    glowMote(ctx, x, fy - 10, 70 * (0.6 + p * 0.6), FIN_DEEP, 0.75 * grow * vis)
    glowMote(ctx, x, fy - 6, 34, FIN_MID, 0.8 * grow * vis)
    glowMote(ctx, x, fy - 4, 14, FIN_CORE, 0.9 * grow * vis)
    for (let i = 0; i < 13; i += 1) {
      const seed = 800 + i * 5.9
      const ang = -Math.PI / 2 + (i / 12 - 0.5) * 2.5 + (hash01(seed) - 0.5) * 0.18
      const l = (60 + hash01(seed + 1) * 90) * (Math.abs(i / 12 - 0.5) < 0.2 ? 1.25 : 0.8) * grow
      const bx = x + Math.cos(ang) * 6
      flameTongue(ctx, bx, fy - 2, Math.cos(ang), Math.sin(ang), l, 16 + hash01(seed + 2) * 12,
        t * 9 + seed, 0.9 * grow * vis, FIN_CORE, FIN_MID, FIN_DEEP)
    }
  }
  // 床に寝た紫の照り (面・床の遠近で潰す)。
  const floorA = ageMs < 60 ? ageMs / 60 : 1 - clamp01((ageMs - 60) / (IMPACT_LIFE_MS - 60))
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.translate(x, fy)
  ctx.scale(1, 0.22)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 120)
  g.addColorStop(0, `rgba(176,96,236,${0.55 * floorA * vis})`)
  g.addColorStop(0.5, `rgba(92,24,150,${0.3 * floorA * vis})`)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, 120, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  // 石のかけら: 放物線で飛んで床に落ち、そこで止まって薄れる。
  const sec = ageMs / 1000
  for (let i = 0; i < 11; i += 1) {
    const seed = 900 + i * 3.7
    const vx = (hash01(seed) - 0.5) * 520
    const vy = -(180 + hash01(seed + 1) * 340)
    const g2 = 1500
    const tLand = (-2 * vy) / g2
    const ts = Math.min(sec, tLand)
    const px = x + vx * ts
    const py = Math.min(fy - 2, fy - 4 + vy * ts + 0.5 * g2 * ts * ts)
    const r = 3.5 + hash01(seed + 2) * 6
    const rot = (hash01(seed + 3) - 0.5) * 12 * ts
    const a = 1 - clamp01((ageMs - 500) / (IMPACT_LIFE_MS - 500))
    stoneShard(ctx, px, py, r, rot + seed, seed, a * vis, FIN_DEEP)
  }
  // 残り火: 床の上に小さな紫の炎がちろちろ残る。
  if (ageMs > 120) {
    const a = clamp01((ageMs - 120) / 120) * (1 - clamp01((ageMs - 400) / (IMPACT_LIFE_MS - 400)))
    for (let i = 0; i < 7; i += 1) {
      const seed = 1100 + i * 4.3
      const bx = x + (hash01(seed) - 0.5) * 170
      const fl = (18 + hash01(seed + 1) * 22) * (0.7 + 0.3 * Math.sin(t * 11 + seed))
      flameTongue(ctx, bx, fy - 1, (hash01(seed + 2) - 0.5) * 0.4, -1, fl, 9 + hash01(seed + 3) * 6,
        t * 8 + seed, 0.75 * a * vis, FIN_CORE, FIN_MID, FIN_DEEP)
    }
    for (let i = 0; i < 9; i += 1) {
      const seed = 1300 + i * 6.1
      const life = (t * (0.8 + hash01(seed) * 0.5) + hash01(seed + 1)) % 1
      fleck(ctx, x + (hash01(seed + 2) - 0.5) * 200, fy - 8 - life * 70, 1.8 + hash01(seed + 3) * 2,
        t * 2 + seed, FIN_FLECK, a * (1 - life) * vis)
    }
  }
}

// ---------------------------------------------------------------- 執行の描き直し (10-20 2回目の答え = 手本 prevA)
// そざい/concept/exec-fx-1020/finisher_a.png。上の v1 は炎の舌が太く明るく、刃を桃白の塊で覆っていた。
//   手本: 刃の両の縁を細い紫の炎がなめる。ところどころ途切れ、縁から少し離れて揺れる。芯は白くせず濃い紫のまま。
//   まわりに小さな紫の火の粉がたくさん漂う。床を打つと細い紫の炎が床から放射状に噴き、黒い瓦礫が飛んで残り火が残る。
const A_CORE = 'rgba(214,160,255,0.9)'
const A_MID = 'rgba(158,72,236,0.8)'
const A_DEEP = 'rgba(86,22,150,0.5)'
const A_SPARK = '#b878ff'

function drawBladeFlame(
  ctx: CanvasRenderingContext2D, base: { x: number; y: number }, tip: { x: number; y: number },
  half: number, strength: number, t: number, flick: boolean,
): void {
  const len = Math.hypot(tip.x - base.x, tip.y - base.y)
  if (len < 4) return
  const ux = (tip.x - base.x) / len, uy = (tip.y - base.y) / len
  const nx = -uy, ny = ux
  // 刃の縁に沿った紫の照り (炎の下地。刃の面は覆わず、両の縁の外側だけ)。
  for (let i = 0; i <= 8; i += 1) {
    const s = 0.08 + (i / 8) * 0.86
    for (const side of [1, -1]) {
      glowMote(ctx, base.x + ux * len * s + nx * half * 1.05 * side, base.y + uy * len * s + ny * half * 1.05 * side,
        half * 1.5, A_MID, 0.22 * strength)
    }
  }
  // 両の縁をうねる炎がなめる。縁に沿って密に立ち、1本ずつ時間で灯ったり消えたりして途切れが動く。
  // 10-20 撮影: 細く離しすぎてゲームの大きさではほぼ見えなかった → 縁へ寄せ、太く濃く。
  const count = 40
  for (let i = 0; i < count; i += 1) {
    const seed = 200 + i * 9.1
    const s = 0.04 + (i / (count - 1)) * 0.94
    const side = i % 2 === 0 ? 1 : -1
    const life = Math.sin(t * (2.2 + hash01(seed) * 2.4) + seed * 3.1)
    if (life < -0.45) continue // 途切れ
    const on = clamp01((life + 0.45) / 0.45)
    const gap = half * (0.7 + hash01(seed + 1) * 0.4) // 縁にほぼ沿う
    const x = base.x + ux * len * s + nx * gap * side
    const y = base.y + uy * len * s + ny * gap * side
    const taper = s > 0.82 ? 1 - (s - 0.82) / 0.18 * 0.5 : 1
    const fl = Math.max(14, half * (1.8 + hash01(seed + 2) * 2.2)) * (0.75 + 0.25 * Math.sin(t * 11 + seed)) * taper
    // 炎は上へ昇り、縁の外へ少し開く (払いの間は後ろへ流れる)。
    const dx = nx * side * 0.45 + (flick ? -ux * 0.45 : 0)
    flameTongue(ctx, x, y, dx, -1, fl, Math.max(6, half * (0.7 + hash01(seed + 3) * 0.5)), t * 8 + seed,
      strength * on * (0.85 + hash01(seed + 4) * 0.3), A_CORE, A_MID, A_DEEP)
  }
  // 漂う紫の火の粉 (刃のまわりに散らばって昇る)。
  const sparks = flick ? 26 : 22
  for (let i = 0; i < sparks; i += 1) {
    const seed = 700 + i * 5.3
    const life = (t * (0.5 + hash01(seed) * 0.5) + hash01(seed + 1)) % 1
    const s = 0.05 + hash01(seed + 2) * 0.95
    const spread = half * (1.5 + hash01(seed + 3) * 4) * (hash01(seed + 4) < 0.5 ? -1 : 1)
    const x = base.x + ux * len * s + nx * spread + Math.sin(t * 2 + seed) * 6 + (flick ? -ux * 30 * life : 0)
    const y = base.y + uy * len * s - life * (30 + hash01(seed + 5) * 50)
    fleck(ctx, x, y, 2 + hash01(seed + 6) * 2.4, t * 2 + seed, A_SPARK, strength * Math.sin(Math.PI * life))
  }
}

function drawFinisherImpact(ctx: CanvasRenderingContext2D, at: Impact, ageMs: number, t: number, vis: number): void {
  const fy = at.floorY
  const x = at.x
  // 噴き上がり: 細い紫の炎が床から放射状に噴いて縮む。芯は白くしない。
  if (ageMs < BURST_MS + 120) {
    const grow = ageMs < 45 ? ageMs / 45 : 1 - clamp01((ageMs - 45) / (BURST_MS + 75)) ** 1.3
    glowMote(ctx, x, fy - 10, 80, A_DEEP, 0.7 * grow * vis)
    glowMote(ctx, x, fy - 4, 30, A_MID, 0.6 * grow * vis)
    for (let i = 0; i < 26; i += 1) {
      const seed = 1500 + i * 4.7
      const ang = -Math.PI / 2 + (i / 25 - 0.5) * 2.9 + (hash01(seed) - 0.5) * 0.2
      const centre = Math.abs(i / 25 - 0.5) < 0.18
      const l = (50 + hash01(seed + 1) * 100) * (centre ? 1.3 : 0.85) * grow
      flameTongue(ctx, x + Math.cos(ang) * 5, fy - 2, Math.cos(ang), Math.sin(ang), l, 8 + hash01(seed + 2) * 9,
        t * 10 + seed, 1 * grow * vis, A_CORE, A_MID, A_DEEP)
    }
    // 打った瞬間に飛ぶ火の粉。
    for (let i = 0; i < 14; i += 1) {
      const seed = 1700 + i * 3.9
      const ang = -Math.PI / 2 + (hash01(seed) - 0.5) * 2.8
      const d = (ageMs / 1000) * (260 + hash01(seed + 1) * 260)
      fleck(ctx, x + Math.cos(ang) * d, fy - 4 + Math.sin(ang) * d * 0.8, 1.4 + hash01(seed + 2) * 1.8,
        ang, A_SPARK, (1 - clamp01(ageMs / (BURST_MS + 120))) * vis)
    }
  }
  drawImpactGround(ctx, x, fy, ageMs, vis)
  // 残り火: 瓦礫のあいだに細い紫の炎がちろちろ残り、火の粉が昇る。
  if (ageMs > 140) {
    const a = clamp01((ageMs - 140) / 120) * (1 - clamp01((ageMs - 420) / (IMPACT_LIFE_MS - 420)))
    for (let i = 0; i < 9; i += 1) {
      const seed = 2100 + i * 4.1
      const bx = x + (hash01(seed) - 0.5) * 180
      const fl = (18 + hash01(seed + 1) * 26) * (0.7 + 0.3 * Math.sin(t * 12 + seed))
      flameTongue(ctx, bx, fy - 1, (hash01(seed + 2) - 0.5) * 0.4, -1, fl, 6 + hash01(seed + 3) * 6,
        t * 9 + seed, 0.95 * a * vis, A_CORE, A_MID, A_DEEP)
    }
    for (let i = 0; i < 10; i += 1) {
      const seed = 2300 + i * 6.7
      const life = (t * (0.7 + hash01(seed) * 0.5) + hash01(seed + 1)) % 1
      fleck(ctx, x + (hash01(seed + 2) - 0.5) * 200, fy - 6 - life * 60, 1.1 + hash01(seed + 3) * 1.4,
        t * 2 + seed, A_SPARK, a * Math.sin(Math.PI * life) * vis)
    }
  }
}

/** 床に寝た紫の照りと、放物線で飛んで床に落ちる黒い瓦礫 (コードの版・絵の版で共通)。 */
function drawImpactGround(ctx: CanvasRenderingContext2D, x: number, fy: number, ageMs: number, vis: number): void {
  // 床に寝た紫の照り (薄く)。
  const floorA = ageMs < 60 ? ageMs / 60 : 1 - clamp01((ageMs - 60) / (IMPACT_LIFE_MS - 60))
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.translate(x, fy)
  ctx.scale(1, 0.2)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 100)
  g.addColorStop(0, `rgba(158,72,236,${0.4 * floorA * vis})`)
  g.addColorStop(0.55, `rgba(86,22,150,${0.22 * floorA * vis})`)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, 100, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  // 黒い瓦礫: 放物線で飛んで床に落ち、そこで止まって薄れる。
  const sec = ageMs / 1000
  for (let i = 0; i < 14; i += 1) {
    const seed = 1900 + i * 3.3
    const vx = (hash01(seed) - 0.5) * 480
    const vy = -(160 + hash01(seed + 1) * 320)
    const g2 = 1500
    const ts = Math.min(sec, (-2 * vy) / g2)
    const px = x + vx * ts
    const py = Math.min(fy - 2, fy - 4 + vy * ts + 0.5 * g2 * ts * ts)
    const a = 1 - clamp01((ageMs - 550) / (IMPACT_LIFE_MS - 550))
    stoneShard(ctx, px, py, 3 + hash01(seed + 2) * 7, (hash01(seed + 3) - 0.5) * 12 * ts + seed, seed, a * vis, A_DEEP)
  }
}

// ---------------------------------------------------------------- 執行・絵の版 (10-20 3回目・src/exec_fx_sprites.ts)
// 炎の質感は手描きの絵、置き方と時間はコード。前のコードの版の「同じ炎が刃の両縁にびっしり」をやめ、
// 刃に灯る炎は5つだけ・大きさに差をつけ・刃先がいちばん大きい。予告では刃先から順に灯る。
const BLADE_FLAMES: readonly { s: number; kind: number; size: number; side: number; at: number }[] = [
  { s: 0.95, kind: 0, size: 6.2, side: 0, at: 0 },     // 刃先の大きな炎
  { s: 0.78, kind: 3, size: 4.2, side: -1, at: 0.2 },  // 二股
  { s: 0.6, kind: 1, size: 3.4, side: 1, at: 0.4 },    // 中
  { s: 0.44, kind: 2, size: 2.2, side: -1, at: 0.6 },  // 小
  { s: 0.3, kind: 2, size: 1.7, side: 1, at: 0.75 },   // 小
]

function drawBladeFlameSprite(
  ctx: CanvasRenderingContext2D, base: { x: number; y: number }, tip: { x: number; y: number },
  half: number, strength: number, t: number, flick: boolean,
): void {
  const len = Math.hypot(tip.x - base.x, tip.y - base.y)
  if (len < 4) return
  const ux = (tip.x - base.x) / len, uy = (tip.y - base.y) / len
  const nx = -uy, ny = ux
  // 払いの間は炎が後ろ (刃の根元の向き) へなびく。立っている時は真上。
  const lean = flick ? Math.atan2(-ux, 1) * 0.55 : 0
  BLADE_FLAMES.forEach((f, i) => {
    const on = clamp01((strength - f.at * 0.8) / 0.25)
    if (on <= 0.01) return
    const frame = Math.floor(t * 9 + i * 1.7)
    const img = flameSprite(f.kind, frame)
    if (!img) return
    const h = Math.max(18, half * f.size) * (0.6 + 0.4 * on) * (0.94 + 0.06 * Math.sin(t * 7 + i * 2.1))
    const w = h * (img.width / img.height)
    const x = base.x + ux * len * f.s + nx * half * 0.55 * f.side
    const y = base.y + uy * len * f.s + ny * half * 0.55 * f.side
    drawAdditive(ctx, img, x, y, w, h, 0.5, FLAME_ANCHOR_Y, strength * on, lean, i % 2 === 1)
  })
  // 火の粉は少しだけ (刃先のまわり)。
  for (let i = 0; i < 5; i += 1) {
    const seed = 760 + i * 5.3
    const life = (t * (0.6 + hash01(seed) * 0.5) + hash01(seed + 1)) % 1
    const s = 0.7 + hash01(seed + 2) * 0.3
    const x = base.x + ux * len * s + nx * half * (hash01(seed + 3) - 0.5) * 5 + (flick ? -ux * 30 * life : 0)
    const y = base.y + uy * len * s - life * (30 + hash01(seed + 5) * 40)
    fleck(ctx, x, y, 1.8 + hash01(seed + 6) * 1.8, t * 2 + seed, A_SPARK, strength * Math.sin(Math.PI * life))
  }
}

// 床の4段 (噴く・這う・ちろちろ・燃え残り) の切り替わり (打ってからの ms)。隣の段へは重ねて溶かす。
const BURST_STAGE_MS = [0, 150, 380, 660, IMPACT_LIFE_MS] as const
const BURST_WIDTH = 380

function drawFinisherImpactSprite(ctx: CanvasRenderingContext2D, at: Impact, ageMs: number, t: number, vis: number): void {
  void t
  const fy = at.floorY
  const x = at.x
  drawImpactGround(ctx, x, fy, ageMs, vis)
  let stage = 0
  while (stage < BURST_FRAMES - 1 && ageMs >= BURST_STAGE_MS[stage + 1]!) stage += 1
  const from = BURST_STAGE_MS[stage]!, to = BURST_STAGE_MS[stage + 1]!
  const p = clamp01((ageMs - from) / Math.max(1, to - from))
  const blend = stage < BURST_FRAMES - 1 ? clamp01((p - 0.6) / 0.4) : 0
  // 打った瞬間 (45ms) は縦に伸び上がる。最後の段は薄れて消える。
  const rise = ageMs < 45 ? 0.55 + 0.45 * (ageMs / 45) : 1
  const fade = stage === BURST_FRAMES - 1 ? 1 - p : 1
  const put = (i: number, a: number): void => {
    const img = burstSprite(i)
    if (!img || a <= 0.01) return
    const h = BURST_WIDTH * (img.height / img.width)
    drawAdditive(ctx, img, x, fy, BURST_WIDTH, h * (i === 0 ? rise : 1), 0.5, BURST_ANCHOR_Y, a * vis)
  }
  put(stage, (1 - blend) * fade)
  if (blend > 0) put(stage + 1, blend)
  // 打った瞬間の火の粉 (絵の火の粉に動きを足す・少なめ)。
  if (ageMs < BURST_MS + 120) {
    for (let i = 0; i < 8; i += 1) {
      const seed = 1700 + i * 3.9
      const ang = -Math.PI / 2 + (hash01(seed) - 0.5) * 2.6
      const d = (ageMs / 1000) * (260 + hash01(seed + 1) * 260)
      fleck(ctx, x + Math.cos(ang) * d, fy - 4 + Math.sin(ang) * d * 0.8, 1.4 + hash01(seed + 2) * 1.8,
        ang, A_SPARK, (1 - clamp01(ageMs / (BURST_MS + 120))) * vis)
    }
  }
}

// ---------------------------------------------------------------- 砲の弾 (C「紫の残り火」)

// 直射・速射は弾ける弾 ('parry' の色・比較版 ?purpleSwap で写す)。
const SHOT_CORE = (): string => `rgba(${et('parry', '232,212,255')},0.95)`
const SHOT_MID = (): string => `rgba(${et('parry', '170,92,232')},0.85)`
const SHOT_DEEP = (): string => `rgba(${et('parry', '80,20,130')},0.5)`
const SHOT_FLECK = (): string => et('parry', '#c48cff')

/** 弾の本体。center = 当たりの中心、directionRad = 進む向き、size = 当たりの幅 (px)。 */
export function drawEmberShot(
  ctx: CanvasRenderingContext2D, center: { x: number; y: number }, directionRad: number,
  size: number, seedBase: number, elapsedMs: number, timeSec: number, reflected: boolean,
): void {
  const r = size * 0.42
  const t = timeSec
  ctx.save()
  ctx.translate(center.x, center.y)
  ctx.rotate(directionRad)
  // 後ろの紫の照り (暗い床でも弾の場所が読めるように)。
  glowMote(ctx, -r * 0.8, 0, r * 2.8, SHOT_DEEP(), 0.55)
  glowMote(ctx, -r * 0.3, 0, r * 1.7, SHOT_MID(), 0.35)
  // 後ろへたなびく炎の尾。
  for (let i = 0; i < 6; i += 1) {
    const seed = seedBase + i * 3.1
    const side = (i / 5 - 0.5) * 1.5
    const fl = r * (2.4 + hash01(seed) * 2.2) * (0.8 + 0.2 * Math.sin(t * 13 + seed)) * (Math.abs(side) < 0.4 ? 1.25 : 0.9)
    flameTongue(ctx, -r * 0.2, side * r * 0.75, -1, side * 0.35, fl, r * (1.1 + hash01(seed + 1) * 0.5),
      t * 10 + seed, 0.85, SHOT_CORE(), SHOT_MID(), SHOT_DEEP())
  }
  // 散る紫の欠片 (弾より遅く流れて置いていかれる)。
  for (let i = 0; i < 7; i += 1) {
    const seed = seedBase + 40 + i * 5.3
    const life = ((elapsedMs + i * 47) % 330) / 330
    const back = r * 1.2 + life * (60 + hash01(seed) * 40)
    const side = (hash01(seed + 1) - 0.5) * r * 1.6 + (hash01(seed + 2) - 0.5) * 34 * life
    fleck(ctx, -back, side - life * 10, 1.6 + hash01(seed + 3) * 1.8, seed + t * 4, SHOT_FLECK(), (1 - life) * 0.95)
  }
  if (reflected) {
    // 弾いた後: 球は割れて黒い石片の群れになり、炎をまとって返っていく。
    for (let i = 0; i < 4; i += 1) {
      const seed = seedBase + 70 + i * 2.9
      const a = (i / 4) * Math.PI * 2 + t * 6
      stoneShard(ctx, Math.cos(a) * r * 0.6, Math.sin(a) * r * 0.6, r * (0.45 + hash01(seed) * 0.2), a * 1.3, seed, 1, SHOT_MID())
    }
    ctx.restore()
    return
  }
  // 黒い鉄の球 (通常合成)。前上から光が当たる。
  const g = ctx.createRadialGradient(r * 0.3, -r * 0.35, r * 0.1, 0, 0, r)
  g.addColorStop(0, '#5a5562')
  g.addColorStop(0.45, '#26232c')
  g.addColorStop(1, '#09080b')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, r, 0, Math.PI * 2)
  ctx.fill()
  // 球の縁の紫の照り返し (面の輪・後ろ側を強く)。
  ctx.globalCompositeOperation = 'lighter'
  const rim = ctx.createRadialGradient(-r * 0.25, 0, r * 0.7, -r * 0.25, 0, r * 1.25)
  rim.addColorStop(0, 'rgba(0,0,0,0)')
  rim.addColorStop(0.45, SHOT_MID())
  rim.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.globalAlpha = 0.6
  ctx.fillStyle = rim
  ctx.beginPath()
  ctx.arc(-r * 0.25, 0, r * 1.25, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export const SHOT_BREAK_LIFE_SEC = 0.36

/** 弾いた瞬間: 球が割れて黒い石片が飛び、紫の炎の欠片が散る。origin = 弾いた所。 */
export function drawEmberShotBreak(
  ctx: CanvasRenderingContext2D, origin: { x: number; y: number }, ageSec: number, seedBase: number,
): void {
  if (ageSec < 0 || ageSec >= SHOT_BREAK_LIFE_SEC) return
  const p = ageSec / SHOT_BREAK_LIFE_SEC
  const fade = (1 - p) ** 1.5
  glowMote(ctx, origin.x, origin.y, 46 * (0.6 + p * 0.6), SHOT_DEEP(), 0.7 * fade)
  glowMote(ctx, origin.x, origin.y, 20, SHOT_CORE(), 0.8 * (1 - clamp01(p * 3)))
  for (let i = 0; i < 9; i += 1) {
    const seed = seedBase + 300 + i * 4.7
    const ang = hash01(seed) * Math.PI * 2
    const sp = 120 + hash01(seed + 1) * 220
    const x = origin.x + Math.cos(ang) * sp * ageSec
    const y = origin.y + Math.sin(ang) * sp * ageSec + 600 * ageSec * ageSec
    flameTongue(ctx, x, y, -Math.cos(ang), -Math.sin(ang), 14 + hash01(seed + 2) * 16, 8,
      seed + ageSec * 20, 0.8 * fade, SHOT_CORE(), SHOT_MID(), SHOT_DEEP())
  }
  for (let i = 0; i < 6; i += 1) {
    const seed = seedBase + 400 + i * 6.3
    const ang = -Math.PI / 2 + (hash01(seed) - 0.5) * 2.6
    const sp = 140 + hash01(seed + 1) * 160
    const x = origin.x + Math.cos(ang) * sp * ageSec
    const y = origin.y + Math.sin(ang) * sp * ageSec + 900 * ageSec * ageSec
    stoneShard(ctx, x, y, 4 + hash01(seed + 2) * 4, seed + ageSec * 9, seed, fade + 0.2, SHOT_MID())
  }
  for (let i = 0; i < 10; i += 1) {
    const seed = seedBase + 500 + i * 3.3
    const ang = hash01(seed) * Math.PI * 2
    const d = ageSec * (90 + hash01(seed + 1) * 160)
    fleck(ctx, origin.x + Math.cos(ang) * d, origin.y + Math.sin(ang) * d - ageSec * 40,
      1.8 + hash01(seed + 2) * 2, seed + ageSec * 8, SHOT_FLECK(), fade)
  }
}

export const SHOT_MUZZLE_FLAME_MS = 150

/** 撃つ瞬間に砲口から噴く紫の炎。p = 0..1 (SHOT_MUZZLE_FLAME_MS の中の進み)。 */
export function drawShotMuzzleFlame(
  ctx: CanvasRenderingContext2D, at: { x: number; y: number }, p: number, dirX: number, timeSec: number,
): void {
  if (p < 0 || p >= 1) return
  const grow = p < 0.2 ? p / 0.2 : 1 - ((p - 0.2) / 0.8) ** 1.2
  glowMote(ctx, at.x, at.y, 40, SHOT_DEEP(), 0.7 * grow)
  glowMote(ctx, at.x, at.y, 16, SHOT_CORE(), 0.85 * grow)
  for (let i = 0; i < 7; i += 1) {
    const seed = 2000 + i * 3.9
    const ang = (i / 6 - 0.5) * 1.3 + (hash01(seed) - 0.5) * 0.2
    const l = (34 + hash01(seed + 1) * 40) * (Math.abs(i / 6 - 0.5) < 0.2 ? 1.3 : 0.85) * grow
    flameTongue(ctx, at.x, at.y, Math.cos(ang) * dirX, Math.sin(ang), l, 13 + hash01(seed + 2) * 8,
      timeSec * 10 + seed, 0.9 * grow, SHOT_CORE(), SHOT_MID(), SHOT_DEEP())
  }
  for (let i = 0; i < 6; i += 1) {
    const seed = 2100 + i * 5.1
    const ang = (hash01(seed) - 0.5) * 1.8
    const d = p * (30 + hash01(seed + 1) * 50)
    fleck(ctx, at.x + Math.cos(ang) * d * dirX, at.y + Math.sin(ang) * d - p * 12, 1.8 + hash01(seed + 2) * 1.6,
      seed, SHOT_FLECK(), 1 - p)
  }
}
